import { TelemetryActivity } from '..';
import { logGenericToConsole, logErrorToConsole } from './utils/logToConsole';
import registerDebug from 'debug';
import { getElapsedTime } from './utils/getCurrentTime';
import { generateUniqueId } from './utils/generateUniqueId';
import { alwaysSendCondition } from '../telemetryProcessor/utils/telemetryUtilities';
import { capitalize } from './utils/capitalize';
import { removeSensitiveFields } from './utils/compliance';
export const parent = registerDebug('mfs');
/**
 * Top-level logger class that implements EventLogger. This class abstracts the telemetry logging technology and
 * implements event caching
 */
export class MfsLogger {
    constructor(component, telemetryProcessor, bufferContainer) {
        this.bufferContainer = bufferContainer;
        this.addToBuffer = () => {
            return !this.telemetryProcessor || this.telemetryProcessor.toBuffer();
        };
        this.component = component;
        this.telemetryProcessor = telemetryProcessor;
    }
    /** Returns whether console logging is enabled or not */
    isConsoleLoggingEnabled() {
        const context = this.telemetryProcessor.getContext();
        return context ? context.serviceLoggingInfo.isConsoleLoggingEnabled : false;
    }
    /** @inheritdoc */
    logCustomMetric(metricName, message, metricData, operationName, logParams, callTimestamp) {
        const scrubbedLogParams = removeSensitiveFields(logParams);
        if (this.isConsoleLoggingEnabled()) {
            logGenericToConsole("metric" /* METRIC */, message, this.component, scrubbedLogParams);
        }
        const timestamp = callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now();
        const sendEvent = () => {
            var _a, _b;
            if ((_a = this.telemetryProcessor) === null || _a === void 0 ? void 0 : _a.loggerIsInitialized()) {
                (_b = this.telemetryProcessor) === null || _b === void 0 ? void 0 : _b.sendCustomTelemetryEvent(metricName, "metric" /* METRIC */, operationName, this.component, message, metricData, scrubbedLogParams, timestamp);
            }
            else if (this.addToBuffer()) {
                this.bufferContainer.metricBuffer.add(sendEvent);
            }
        };
        sendEvent();
    }
    /** @inheritdoc */
    logInfo(eventName, message, operationName, logParams, callTimestamp) {
        const scrubbedLogParams = removeSensitiveFields(logParams);
        if (this.isConsoleLoggingEnabled()) {
            logGenericToConsole("info" /* INFO */, message, this.component, scrubbedLogParams);
        }
        const timestamp = callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now();
        const sendEvent = () => {
            var _a, _b;
            if ((_a = this.telemetryProcessor) === null || _a === void 0 ? void 0 : _a.loggerIsInitialized()) {
                (_b = this.telemetryProcessor) === null || _b === void 0 ? void 0 : _b.sendTelemetryEvent(eventName, "info" /* INFO */, operationName, this.component, message, scrubbedLogParams, timestamp);
            }
            else if (this.addToBuffer()) {
                this.bufferContainer.infoBuffer.add(sendEvent);
            }
        };
        sendEvent();
    }
    /** @inheritdoc */
    logVerbose(operationName, message, logParams, callTimestamp) {
        const scrubbedLogParams = removeSensitiveFields(logParams);
        if (this.isConsoleLoggingEnabled()) {
            logGenericToConsole("verbose" /* VERBOSE */, message, this.component, scrubbedLogParams);
        }
        this.bufferContainer.verboseBuffer.enq({
            op: operationName,
            msg: message,
            params: scrubbedLogParams,
            tm: new Date(callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now()),
        });
    }
    /** @inheritdoc */
    logWarn(eventName, mfsStatusCode, { errorType, message, error }, operationName, logParams, callTimestamp) {
        const scrubbedLogParams = removeSensitiveFields(logParams);
        const errorObject = this.getLogErrorInfo({ errorType, message, error });
        logErrorToConsole("warn" /* WARN */, this.component, errorObject, scrubbedLogParams);
        const timestamp = callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now();
        const sendEvent = () => {
            var _a, _b;
            if ((_a = this.telemetryProcessor) === null || _a === void 0 ? void 0 : _a.loggerIsInitialized()) {
                (_b = this.telemetryProcessor) === null || _b === void 0 ? void 0 : _b.sendTelemetryErrorEvent(capitalize(eventName), "warn" /* WARN */, operationName, this.component, errorObject, mfsStatusCode, undefined, scrubbedLogParams, timestamp);
            }
            else if (this.addToBuffer()) {
                this.bufferContainer.warnBuffer.add(sendEvent);
            }
        };
        sendEvent();
    }
    /** @inheritdoc */
    logError(eventName, mfsStatusCode, { errorType, message, error }, operationName, logParams, callTimestamp) {
        const scrubbedLogParams = removeSensitiveFields(logParams);
        const errorObject = this.getLogErrorInfo({ errorType, message, error });
        logErrorToConsole("error" /* ERROR */, this.component, errorObject, scrubbedLogParams);
        const verboseLogs = this.bufferContainer.getVerboseLogsToSend();
        const timestamp = callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now();
        const sendEvent = () => {
            var _a, _b;
            if ((_a = this.telemetryProcessor) === null || _a === void 0 ? void 0 : _a.loggerIsInitialized()) {
                (_b = this.telemetryProcessor) === null || _b === void 0 ? void 0 : _b.sendTelemetryErrorEvent(capitalize(eventName), "error" /* ERROR */, operationName, this.component, errorObject, mfsStatusCode, verboseLogs, scrubbedLogParams, timestamp);
            }
            else if (this.addToBuffer()) {
                this.bufferContainer.errorBuffer.add(() => sendEvent());
            }
        };
        sendEvent();
    }
    /** @inheritdoc */
    startActivity(activityName, activityEventType, message, operationName, logParams) {
        if (this.isConsoleLoggingEnabled()) {
            logGenericToConsole("info" /* INFO */, message, this.component, {
                activityName: activityName,
                operation: operationName,
                description: message,
                logParams: removeSensitiveFields(logParams),
            });
        }
        const correlationId = generateUniqueId();
        const startTime = getElapsedTime();
        return new TelemetryActivity(activityName, activityEventType, startTime, operationName, this.component, correlationId, message);
    }
    /** @inheritdoc */
    endActivitySuccess(activityObject, shouldSend, endTime, callTimestamp) {
        const currentTime = getElapsedTime();
        if (this.isConsoleLoggingEnabled()) {
            logGenericToConsole("info" /* INFO */, 'Activity succeeded: %O', this.component, {
                eventName: activityObject.activityName,
                duration: currentTime - activityObject.startTime,
                result: 'success',
                operationName: activityObject.operationName,
                correlationId: activityObject.correlationId,
            });
        }
        const timestamp = callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now();
        const endTimeCalc = endTime !== null && endTime !== void 0 ? endTime : currentTime;
        const sendEvent = () => {
            var _a, _b;
            if ((_a = this.telemetryProcessor) === null || _a === void 0 ? void 0 : _a.loggerIsInitialized()) {
                (_b = this.telemetryProcessor) === null || _b === void 0 ? void 0 : _b.sendActivityEvent('success', activityObject, shouldSend, undefined, undefined, endTimeCalc, timestamp);
            }
            else if (this.addToBuffer()) {
                this.bufferContainer.activityBuffer.add(sendEvent);
            }
        };
        sendEvent();
    }
    /** @inheritdoc */
    endActivityFailure(activityObject, error, endTime, callTimestamp) {
        const errorInfoObject = error
            ? {
                errorType: 'exception',
                message: error.message,
                error: error,
            }
            : undefined;
        if (errorInfoObject) {
            this.logActivityErrorToConsole(activityObject, errorInfoObject);
        }
        const verboseLogs = this.bufferContainer.getVerboseLogsToSend();
        const endTimeCalc = endTime !== null && endTime !== void 0 ? endTime : getElapsedTime();
        const timestamp = callTimestamp !== null && callTimestamp !== void 0 ? callTimestamp : Date.now();
        const sendEvent = () => {
            var _a, _b;
            if ((_a = this.telemetryProcessor) === null || _a === void 0 ? void 0 : _a.loggerIsInitialized()) {
                (_b = this.telemetryProcessor) === null || _b === void 0 ? void 0 : _b.sendActivityEvent('failure', activityObject, alwaysSendCondition, errorInfoObject, verboseLogs, endTimeCalc, timestamp);
            }
            else if (this.addToBuffer()) {
                this.bufferContainer.activityBuffer.add(sendEvent);
            }
        };
        sendEvent();
    }
    /**
     * Method to log activity errors to the console
     *
     * @param activityObject Activity that is being logged.
     * @param errorObject Error object from activity.
     */
    logActivityErrorToConsole(activityObject, errorObject) {
        if (!(errorObject === null || errorObject === void 0 ? void 0 : errorObject.errorType) || (errorObject === null || errorObject === void 0 ? void 0 : errorObject.errorType) == 'warning') {
            logErrorToConsole("warn" /* WARN */, this.component, errorObject);
        }
        else if ((errorObject === null || errorObject === void 0 ? void 0 : errorObject.errorType) == 'exception') {
            logErrorToConsole("error" /* ERROR */, this.component, errorObject);
        }
        else if (this.isConsoleLoggingEnabled()) {
            logGenericToConsole("info" /* INFO */, 'Activity failed: %O', this.component, {
                eventName: activityObject === null || activityObject === void 0 ? void 0 : activityObject.activityName,
                duration: getElapsedTime() - activityObject.startTime,
                result: 'failure',
                operationName: activityObject.operationName,
                correlationId: activityObject.correlationId,
            });
        }
    }
    /**
     * Method to get ErrorInfo for used for logging errors
     *
     * @param errorInfo Error Info parameters to be used to create a complete error info.
     * @returns Completed Error Info object.
     */
    getLogErrorInfo(errorInfo) {
        var _a, _b;
        return {
            errorType: errorInfo.errorType,
            message: (_a = errorInfo.message) !== null && _a !== void 0 ? _a : (_b = errorInfo.error) === null || _b === void 0 ? void 0 : _b.message,
            error: errorInfo.error,
        };
    }
}
//# sourceMappingURL=mfsLogger.js.map